import React, { useState, useMemo, useEffect } from "react";
import {
  Container,
  ProjectCard,
  ProjectItem,
  ProjectField,
  EmptyMessage,
  Pagination,
  BtnPagination,
  BtnClose,
  BtnNav,
  Title,
  TitleField,
  ValueField,
  DateFilterContainer,
  DateInputWrapper,
  DateInput,
  DateLabel
} from './styles';

const ShowProject = ({ projects, onClose }) => {
  const [page, setPage] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const itemsPerPage = 1;

  const projectList = useMemo(() => 
    Array.isArray(projects) ? projects : [], 
    [projects]
  );

  const filteredProjects = useMemo(() => {
    return projectList.filter((project) => {
      if (!project?.date) return true;
      const d = new Date(project.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }, [projectList, startDate, endDate]);

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const currentItems = filteredProjects.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage
  );

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!projects) {
    return (
      <Container onClick={handleOverlayClick}>
        <ProjectCard>
          <BtnClose onClick={onClose}>✖</BtnClose>
          <Title>Detalhes do Projeto</Title>
          <EmptyMessage>
            <i className="fas fa-spinner fa-spin"></i>
            Carregando dados...
          </EmptyMessage>
        </ProjectCard>
      </Container>
    );
  }

  return (
    <Container onClick={handleOverlayClick}>
      <ProjectCard>
        <BtnClose onClick={onClose}>✖</BtnClose>
        <Title>Detalhes dos Projetos</Title>

        {projectList.length === 0 ? (
          <EmptyMessage>
            <i className="fas fa-info-circle"></i>
            Nenhum projeto encontrado para esta conta.
          </EmptyMessage>
        ) : filteredProjects.length > 0 ? (
          currentItems.map((project, index) => (
            <ProjectItem key={project.id || index}>
              {/* Project Type */}
              <ProjectField>
                <TitleField>Project Type:</TitleField>
                <ValueField>{project.project_type || 'N/A'}</ValueField>
              </ProjectField>

              {/* Methodology */}
              <ProjectField>
                <TitleField>Methodology:</TitleField>
                <ValueField>{project.methodology || 'N/A'}</ValueField>
              </ProjectField>

              {/* Country */}
              <ProjectField>
                <TitleField>Country:</TitleField>
                <ValueField>{project.country || 'N/A'}</ValueField>
              </ProjectField>

              {/* Area (hectares) */}
              <ProjectField>
                <TitleField>Area (hectares):</TitleField>
                <ValueField>{project.total_project_area || 'N/A'}</ValueField>
              </ProjectField>

              {/* Project Proponent(s) */}
              <ProjectField>
                <TitleField>Project Proponent(s):</TitleField>
                <ValueField>{project.project_proponents || 'N/A'}</ValueField>
              </ProjectField>

              {/* Start Date */}
              <ProjectField>
                <TitleField>Start Date:</TitleField>
                <ValueField>{project.start_date || 'N/A'}</ValueField>
              </ProjectField>

              {/* Crediting Period */}
              <ProjectField>
                <TitleField>Crediting Period:</TitleField>
                <ValueField>
                  {project.crediting_period_start && project.crediting_period_end 
                    ? `${project.crediting_period_start} - ${project.crediting_period_end}`
                    : 'N/A'
                  }
                </ValueField>
              </ProjectField>

              {/* Validator */}
              <ProjectField>
                <TitleField>Validator:</TitleField>
                <ValueField>{project.validator || 'N/A'}</ValueField>
              </ProjectField>

              {/* Verifier(s) */}
              <ProjectField>
                <TitleField>Verifier(s):</TitleField>
                <ValueField>{project.verifier || 'N/A'}</ValueField>
              </ProjectField>
            </ProjectItem>
          ))
        ) : (
          <EmptyMessage>
            <i className="fas fa-info-circle"></i>
            Nenhum projeto encontrado para o período selecionado.
          </EmptyMessage>
        )}

        {totalPages > 1 && (
          <Pagination>
            <BtnNav disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              {"<"}
            </BtnNav>

            {Array.from({ length: Math.min(3, totalPages) }).map((_, idx) => {
              const start = Math.floor(page / 3) * 3;
              const pageNumber = start + idx;

              if (pageNumber >= totalPages) return null;

              return (
                <BtnPagination
                  key={pageNumber}
                  onClick={() => setPage(pageNumber)}
                  active={page === pageNumber}
                >
                  {pageNumber + 1}
                </BtnPagination>
              );
            })}

            <BtnNav disabled={page === totalPages - 1} onClick={() => setPage((p) => p + 1)}>
              {">"}
            </BtnNav>
          </Pagination>
        )}
      </ProjectCard>
    </Container>
  );
};

export default ShowProject;